/**
 * useInboxV3 Hook
 *
 * Main composed hook that combines all inbox functionality.
 * This hook composes smaller, focused hooks for better maintainability.
 *
 * Individual hooks can also be used directly in components that only need
 * specific functionality.
 */

import {useState, useCallback, useMemo} from 'react'
import {ChatType, RightPanelTab, ConversationData} from '../types/inbox-v3.types'
import {useConversations} from './useConversations'
import {useConversationActions} from './useConversationActions'
import {useConversationSelection} from './useConversationSelection'
import {useSelectedConversation} from './useSelectedConversation'
import {useTeamMembers} from './useTeamMembers'
import {useContactDetails} from './useContactDetails'

export function useInboxV3() {
  // Chat type state (inbox/rms/email)
  const [chatType, setChatType] = useState<ChatType>('inbox')

  // Right panel state
  const [rightPanelTab, setRightPanelTab] = useState<RightPanelTab>('detail')
  const [showRightPanel, setShowRightPanel] = useState(false)

  // Compose individual hooks
  const conversations = useConversations()
  const selection = useConversationSelection()
  const selected = useSelectedConversation({
    conversations: conversations.conversations,
    preferConversationId: false, // Set to true when switching to V3 API format in URLs
  })
  const teamMembers = useTeamMembers()

  // Actions with refresh callback
  const actions = useConversationActions({
    onActionComplete: conversations.refresh,
  })

  // Contact details for selected conversation
  // Always fetch when conversation is selected (needed for assignedTo in header)
  const contact = useContactDetails({
    conversation: selected.selectedConversation,
    autoFetch: true,
  })

  // Get assigned team member for selected conversation
  // Use assignedTo from contact details (fetched from /contact/information endpoint)
  // This is more reliable than checking teamMembers.assignedContacts
  const assignedTo = useMemo(() => {
    const assignedUserId = contact.contactDetails?.assignedTo
    if (!assignedUserId) return null
    return teamMembers.teamMembers.find((member) => member._id === assignedUserId) || null
  }, [contact.contactDetails?.assignedTo, teamMembers.teamMembers])

  // Wrapper to mark conversation as read when selecting
  const selectConversationAndMarkAsRead = useCallback(
    (conversation: ConversationData) => {
      // First, select the conversation (updates URL, state, etc.)
      selected.selectConversation(conversation)

      // If conversation is unread, mark it as read via API
      if (!conversation.contacts.isInboxRead) {
        actions.markAsRead(conversation)
      }
    },
    [selected, actions]
  )

  // Right panel toggle - fetch contact details when opening
  const toggleRightPanel = useCallback(() => {
    setShowRightPanel((prev) => {
      const willShow = !prev
      // Fetch contact details when opening the panel
      if (willShow && selected.selectedConversation) {
        contact.refetch()
      }
      return willShow
    })
  }, [selected.selectedConversation, contact])

  // Helper to archive by ID (finds conversation first)
  const archiveConversation = useCallback(
    (id: string) => {
      const conversation = conversations.conversations.find((c) => c._id === id)
      if (conversation) {
        actions.toggleArchive(conversation)
      }
    },
    [conversations.conversations, actions]
  )

  // Helper to flag by ID (finds conversation first)
  const flagConversation = useCallback(
    (id: string) => {
      const conversation = conversations.conversations.find((c) => c._id === id)
      if (conversation) {
        actions.toggleFlag(conversation)
      }
    },
    [conversations.conversations, actions]
  )

  return {
    // ===== Chat Type =====
    chatType,
    setChatType,

    // ===== Conversations (from useConversations) =====
    conversations: conversations.conversations,
    // For backward compatibility, conversations is also filteredConversations
    // (filtering is now done server-side via API)
    filteredConversations: conversations.conversations,
    openCount: conversations.openCount,
    isLoading: conversations.isLoading,

    // ===== Filters (from useConversations -> useInboxFilters) =====
    searchQuery: conversations.searchQuery,
    setSearchQuery: conversations.setSearchQuery,
    sortOrder: conversations.sortOrder,
    setSortOrder: conversations.setSortOrder,
    toggleSortOrder: conversations.toggleSortOrder,
    // Message filters
    messageFilter: conversations.messageFilter,
    setMessageFilter: conversations.setMessageFilter,
    // Assignee filters
    assigneeFilter: conversations.assigneeFilter,
    setAssigneeFilter: conversations.setAssigneeFilter,
    // Search mode
    searchMode: conversations.searchMode,
    setSearchMode: conversations.setSearchMode,
    // Combined filter state (for QuickFiltersModal)
    filterState: conversations.filterState,
    applyFilters: conversations.applyFilters,
    clearFilters: conversations.clearFilters,
    activeFilterCount: conversations.activeFilterCount,
    // Refresh
    refreshConversations: conversations.refresh,

    // ===== Selected Conversation (from useSelectedConversation) =====
    selectedConversation: selected.selectedConversation,
    selectedReceiver: selected.selectedReceiver,
    selectedConversationId: selected.selectedConversation?._id || null,
    selectConversation: selectConversationAndMarkAsRead,
    mobileView: selected.mobileView,
    setMobileView: selected.setMobileView,
    goBackToList: selected.goBackToList,
    openContactDetails: selected.openContactDetails,
    closeContactDetails: selected.closeContactDetails,

    // ===== Bulk Selection (from useConversationSelection) =====
    selectedConversations: selection.selectedIds,
    isBulkActionsActive: selection.isSelectionActive,
    toggleConversationSelection: (id: string, isSelected: boolean) => {
      if (isSelected) {
        selection.select(id)
      } else {
        selection.deselect(id)
      }
    },
    selectAllConversations: (shouldSelect: boolean) => {
      if (shouldSelect) {
        selection.selectAll(conversations.conversations)
      } else {
        selection.deselectAll()
      }
    },
    clearSelection: selection.deselectAll,

    // ===== Actions (from useConversationActions) =====
    archiveConversation,
    flagConversation,
    // Direct access to action methods
    actions,

    // ===== Team Members (from useTeamMembers) =====
    teamMembers: teamMembers.teamMembers,
    isLoadingTeamMembers: teamMembers.isLoading,
    assignedTo,
    assignConversation: async (conversationId: string, assignee: {_id: string} | null) => {
      // Find the conversation to get contactId and receiver
      const conversation = conversations.conversations.find((c) => c._id === conversationId)
      if (!conversation) {
        console.warn('Cannot assign: conversation not found')
        return
      }
      if (assignee) {
        // contacts._id now contains the actual contact ID (participant.refId)
        await teamMembers.assignConversation({
          contactId: conversation.contacts._id,
          receiver: conversation.receiver,
          assigneeId: assignee._id,
        })
        // Refetch contact details to update assignedTo in UI
        contact.refetch()
      } else {
        // Use actions.unassign which calls PATCH /conversations/:id/flags with {assignedTo: null}
        await actions.unassign(conversation)
        // Refetch contact details to update assignedTo in UI
        contact.refetch()
      }
    },
    unassignConversation: async (conversationId: string) => {
      const conversation = conversations.conversations.find((c) => c._id === conversationId)
      if (conversation) {
        // Use actions.unassign which calls PATCH /conversations/:id/flags with {assignedTo: null}
        await actions.unassign(conversation)
        // Refetch contact details to update assignedTo in UI
        contact.refetch()
      }
    },

    // ===== Contact Details (from useContactDetails) =====
    contactDetails: contact.contactDetails,
    contactStats: contact.contactStats,
    messengerName: contact.displayName,
    isLoadingMessages: false, // TODO: Add when message loading is implemented
    isLoadingContact: contact.isLoading,
    refetchContactDetails: contact.refetch,

    // ===== Right Panel =====
    rightPanelTab,
    setRightPanelTab,
    showRightPanel,
    toggleRightPanel,

    // ===== Placeholder actions (to be implemented) =====
    tagConversation: (id: string) => {
      console.log(`Opening tag modal for conversation ${id}`)
    },
    unsubscribeConversation: (id: string) => {
      const conversation = conversations.conversations.find((c) => c._id === id)
      if (conversation) {
        actions.unsubscribe(conversation)
      }
    },
    subscribeConversation: (id: string) => {
      const conversation = conversations.conversations.find((c) => c._id === id)
      if (conversation) {
        actions.subscribe(conversation)
      }
    },
  }
}